import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import capitalize from "lodash/capitalize";

import {
    RVIcon,
    RVIconSizeOptions,
    Tag,
    RVStatusBadge,
    BADGE_STATUS,
    RVGridActionableCell,
    RVAuditColumn,
    generateFilterData,
    getFilterValue,
    RVGRID_COLUMN_SIZE_OPTIONS,
} from "@regal-voice/ui";
import { parseTriggerNodeData } from "Components/elements/RVFlow/Shared/JourneyNodePresentation";
import { GridColumn, GridColumnsType } from "Types/ComponentTypes/RVGridTypes";
import { Journey, NodeType, NodeTriggerType, JourneyWithMetrics, JourneyStatus } from "Types/Journey";

import { triggerTypeDisplayName } from "../JourneyEditPage/Components/JourneyBuilder/shared/helpers";
import { JourneyStatistics } from "./JourneysUsersCounters/JourneysUsersCounters";
import { TagsColumn } from "./TableColumns";

import styles from "../Journeys.module.scss";

export const columns: ({
    queryParams,
    filterItems,
    tags,
}: {
    queryParams: Record<string, any>;
    filterItems: Record<string, any>;
    tags: Array<Tag>;
}) => GridColumnsType<Journey> = ({
    queryParams,
    filterItems,
    tags,
}: {
    queryParams: Record<string, any>;
    filterItems: Record<string, any>;
    tags: Array<Tag>;
}): Array<GridColumn<Journey>> => {
    return [
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.ID,
            title: "ID #",
            dataIndex: "friendlyId",
            fixed: "left",
            key: "friendlyId",
            // eslint-disable-next-line react/display-name
            render: (friendlyId: string, record: any) => (
                <RVGridActionableCell content={friendlyId} linkTo={record.id} />
            ),
            sorter: true,
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.MEDIUM,
            title: "Journey Name",
            dataIndex: "name",
            // eslint-disable-next-line react/display-name
            render: (text: any, record: Journey) => (
                <RVGridActionableCell
                    prefix={record.hidden && <RVIcon icon="hidden" size={RVIconSizeOptions.SMALL} color="gray-3" />}
                    content={record.name}
                    linkTo={record.id}
                />
            ),
            sorter: true,
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.XSMALL,
            title: "State",
            dataIndex: "state",
            render(text: any) {
                return (
                    <RVStatusBadge
                        status={text == "live" ? BADGE_STATUS.ACTIVE : BADGE_STATUS.PENDING}
                        content={capitalize(text)}
                    />
                );
            },
            sorter: true,
            filterMultiple: true,
            filters: [
                { text: "Draft", value: JourneyStatus.DRAFT },
                { text: "Live", value: JourneyStatus.LIVE },
            ],
            filteredValue: queryParams.filterBy?.filter((value: any) => value.key === "state")?.[0]?.value || null,
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            title: "Trigger",
            dataIndex: "triggerType",
            // eslint-disable-next-line react/display-name
            render: (triggerType: any, record: JourneyWithMetrics) => {
                const { flow } = record;
                const shouldDisplayEventName = ![
                    NodeTriggerType.AddContactToStaticSegment,
                    NodeTriggerType.Scheduled,
                    NodeTriggerType.Pageview,
                ].includes(triggerType);

                if (Array.isArray(flow)) {
                    const triggerNode = flow.find((el) => el.type == NodeType.Trigger);
                    if (triggerNode) {
                        const triggerNodeData = parseTriggerNodeData(triggerNode);
                        return (
                            <div className="flex-column">
                                <span>{triggerTypeDisplayName(triggerType)}</span>
                                {shouldDisplayEventName && (
                                    <span className={styles.eventName}>{triggerNodeData.eventName} </span>
                                )}
                            </div>
                        );
                    }
                }
                return null;
            },
            sorter: true,
            filterMultiple: true,
            filters: [
                {
                    text: triggerTypeDisplayName(NodeTriggerType.AddContactToStaticSegment),
                    value: NodeTriggerType.AddContactToStaticSegment,
                },
                { text: triggerTypeDisplayName(NodeTriggerType.Custom), value: NodeTriggerType.Custom },
                { text: triggerTypeDisplayName(NodeTriggerType.Pageview), value: NodeTriggerType.Pageview },
                { text: triggerTypeDisplayName(NodeTriggerType.RegalVoice), value: NodeTriggerType.RegalVoice },
                { text: triggerTypeDisplayName(NodeTriggerType.Scheduled), value: NodeTriggerType.Scheduled },
            ],
            filteredValue:
                queryParams.filterBy?.filter((value: any) => value.key === "triggerType")?.[0]?.value || null,
        },
        {
            size: RVGRID_COLUMN_SIZE_OPTIONS.SMALL,
            title: (
                <div>
                    <span>Journey Stats</span>
                    <Tooltip title={"Data updates every 15 seconds"}>
                        <InfoCircleOutlined className={styles.info} />
                    </Tooltip>
                </div>
            ),
            dataIndex: "finishedJourneys",
            // eslint-disable-next-line react/display-name
            render: (text: any, record: JourneyWithMetrics) => (
                <JourneyStatistics record={record} key={`${record.id}-${record.originalCreatedAt}-statistics`} />
            ),
            sorter: false,
        },
        TagsColumn({ tags, queryParams }),
        RVAuditColumn<Journey>({
            key: "createdInfo",
            title: "Created",
            timestampField: "originalCreatedAt",
            userField: "createdBy",
            filterOptions: generateFilterData(
                [...new Set(filterItems.createdByNames)],
                (item: string) => ({
                    text: item,
                    value: item,
                }),
                false
            ),
            filterValue: getFilterValue(queryParams, "createdInfo"),
        }),
        RVAuditColumn<Journey>({
            key: "updatedInfo",
            title: "Updated",
            timestampField: "updatedAt",
            userField: "updatedBy",
            filterOptions: generateFilterData(
                [...new Set(filterItems.updatedByNames)],
                (item: string) => ({
                    text: item,
                    value: item,
                }),
                false
            ),
            filterValue: getFilterValue(queryParams, "updatedInfo"),
        }),
    ].map((column: Partial<Journey> & any) => ({
        key: column.key ?? column.dataIndex,
        render: (text: any) => text,
        ...column,
    }));
};
